/**
 * A tiny shared registry mapping a route path to a human label for the
 * browser-style tab bar. Detail pages (a collaborator, a submission, …) register
 * their real title (e.g. the person's name) when they load, so the tab shows
 * "Aakriti Maraseri" instead of a generic "Colaborador". Persisted per browser
 * tab (sessionStorage) so labels survive a refresh; any component can subscribe.
 */
const KEY = 'ciimar-tab-titles';

type Titles = Record<string, string>;

function load(): Titles {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Titles) : {};
  } catch {
    return {};
  }
}

let titles: Titles = load();
const listeners = new Set<() => void>();

export function setTabTitle(path: string, label: string): void {
  const clean = label.trim();
  if (!clean || titles[path] === clean) return;
  titles = { ...titles, [path]: clean };
  try {
    sessionStorage.setItem(KEY, JSON.stringify(titles));
  } catch {
    /* ignore */
  }
  listeners.forEach((l) => l());
}

export function getTabTitle(path: string): string | undefined {
  return titles[path];
}

export function subscribeTabTitles(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** First + last name, for compact tab labels (e.g. "Maria Alberta Costa" → "Maria Costa"). */
export function firstLastName(full: string): string {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 2) return full.trim();
  return `${parts[0]} ${parts[parts.length - 1]}`;
}
